/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Button, Grid, Hidden, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import BLUE_C_LOGO from 'images/articles/coursera-c-logo.svg';
import C_PLUS_BG from 'images/articles/coursera-plus-promo-bg-mobile.svg';
import C_PLUS_LOGO from 'images/articles/coursera-plus-white.png';
import GOOGLE_LOGO from 'images/articles/google.svg';
import IBM_LOGO from 'images/articles/ibm.svg';
import MICROSOFT_LOGO from 'images/articles/microsoft.svg';
import VANDERBILT_LOGO from 'images/articles/vanderbilt.png';

import _t from 'i18n!nls/articles';

const styles = {
  link: css`
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  `,
  banner: css`
    background: url(${BLUE_C_LOGO}) -40% 50% no-repeat,
      linear-gradient(270deg, var(--cds-color-blue-700) 0%, var(--cds-color-blue-950) 100%);
    border-radius: var(--cds-spacing-50);
    margin: var(--cds-spacing-300) auto;
    max-width: 750px;
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
    position: relative;
    align-items: center;
    min-height: 300px;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-400);
      background: url(${C_PLUS_BG}) no-repeat,
        linear-gradient(270deg, var(--cds-color-blue-700) 0%, var(--cds-color-blue-950) 100%);
      background-size: cover;
    }
  `,
  header: css`
    display: flex;
    flex-direction: column;
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  title: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-150) 0 var(--cds-spacing-200);
    }
  `,
  logoWrapper: css`
    display: block;
    line-height: 0;
    margin: 0 !important;

    img {
      margin-top: 0 !important; /* overwritten by the richtext rules */
      width: auto;
      max-width: 104px;
    }
  `,
  listContainer: css`
    gap: 20px;

    &:not(.rc-RichText.rc-RichText) {
      list-style-type: none;
    }
    ${breakpoints.down('xs')} {
      max-width: 100%;
      padding-left: 0 !important;
      gap: 16px;
    }
  `,
  listItem: css`
    background-color: var(--cds-color-white-0);
    height: 44px;
    width: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  `,
  ctaSm: css`
    margin-top: var(--cds-spacing-300);
  `,
};

const CPlusPartnerLogosPromoUnit = () => {
  const isSmUp = useMediaQuery(breakpoints.up('sm'));
  const header = _t('Build job-ready skills with Coursera Plus');
  const ctaText = _t('Start 7-day free trial');
  const renderCTA = (isFullWidth: boolean) => (
    <Button
      variant="primaryInvert"
      icon={<ArrowNextIcon />}
      fullWidth={isFullWidth}
      component={TrackedLink2}
      trackingName="article_c_plus_promo_unit"
      withVisibilityTracking
      href="/courseraplus?trk_ref=articlesCPlusPromoUnit"
      aria-label={ctaText}
      edgeAlign="start"
    >
      {ctaText}
    </Button>
  );

  return (
    <TrackedLink2
      trackingName="article_c_plus_promo_unit"
      withVisibilityTracking
      href="/courseraplus?trk_ref=articlesCPlusPromoUnit"
      css={styles.link}
      aria-label={header + ' ' + ctaText}
    >
      <Grid container justifyContent="space-between" css={styles.banner}>
        <Grid item xs={12} sm={8} css={styles.header}>
          <figure css={styles.logoWrapper}>
            <Imgix src={C_PLUS_LOGO} alt={_t('Coursera Plus')} maxWidth={208} height={20} />
          </figure>
          <Typography2 color="invertBody" variant="titleLarge" component="h5" css={styles.title}>
            {header}
          </Typography2>
          <Hidden xsDown={true}>{renderCTA(false)} </Hidden>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Grid
            css={styles.listContainer}
            component="ul"
            container
            direction={isSmUp ? 'column' : 'row'}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item component="li" css={styles.listItem}>
              <Imgix src={GOOGLE_LOGO} alt={_t('Google')} width={48} maxHeight={48} />
            </Grid>
            <Grid item component="li" css={styles.listItem}>
              <Imgix src={MICROSOFT_LOGO} alt={_t('Microsoft')} width={22} maxHeight={22} />
            </Grid>
            <Grid item component="li" css={styles.listItem}>
              <Imgix src={IBM_LOGO} alt={_t('IBM')} width={48} maxHeight={48} />
            </Grid>
            <Grid item component="li" css={styles.listItem}>
              <Imgix src={VANDERBILT_LOGO} alt={_t('Vanderbilt')} width={32} maxHeight={32} />
            </Grid>
          </Grid>
          <Hidden smUp={true} css={styles.ctaSm}>
            {renderCTA(true)}
          </Hidden>
        </Grid>
      </Grid>
    </TrackedLink2>
  );
};

export default CPlusPartnerLogosPromoUnit;
